import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import * as Api from "@/api";
import ImageFile from "@/app/domain/media/library/Image";
import urls from "@/constants/Urls";
import { toggleLoadingState } from "@/utils/utility";
import Valkyrie, { viTrashCan } from "@sippy-platform/valkyrie";

import {
  Box,
  Button,
  CardMedia,
  Dialog,
  DialogContent,
  DialogTitle,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";

function ImageSelect({ value, select, label, defaultLabel, id }) {
  const pbx = useSelector((state) => state.domain.pbx);
  const imageStore = useSelector((state) => state.domain.images);

  const [open, setOpen] = useState(false);

  useEffect(() => {
    async function getData() {
      toggleLoadingState(true);
      await Promise.resolve(Api.getImages({ context: pbx.context })).finally(() => toggleLoadingState());
    }

    getData();

    return () => toggleLoadingState(false);
  }, [pbx.context]);

  const currentMedia = imageStore.list.find((image) => image.id === value);

  async function setFile(file) {
    select(file.id);
    setOpen(false);
  }

  return (
    <>
      <List>
        <InputLabel
          shrink
          sx={{
            position: "absolute",
            top: -8,
            left: 8,
            px: 0.75,
            backdropFilter: "blur(100px)",
            zIndex: 10,
          }}
        >
          {label}
        </InputLabel>
        <ListItem
          sx={{
            p: 0,
            minHeight: 30,
            "& .MuiListItemSecondaryAction-root": {
              right: "3px !important",
            },
          }}
          secondaryAction={
            value &&
            currentMedia && (
              <CardMedia
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: 0.75,
                }}
                image={`${urls.image}${pbx.context}%2F${currentMedia.id}.${currentMedia.contentType}?alt=media&token=${currentMedia.token}`}
              />
            )
          }
        >
          <ListItemButton
            id={id}
            onClick={() => setOpen(true)}
            sx={(theme) => ({
              py: 0.5,
              px: 1.5,
              m: 0,
              width: 1,
              border: "1px solid rgba(0,0,0,.23)",
              borderRadius: 1,
              transition: "all .2s ease-in-out",
              minHeight: 40,
              height: "auto",
              "&:hover": {
                background: "inherit",
                borderColor: "rgba(0,0,0,0.87)",
              },
              ...theme.applyStyles("dark", {
                borderColor: "rgba(255,255,255,.23)",
                "&:hover": {
                  borderColor: "#fff",
                },
              }),
            })}
          >
            <ListItemText
              primary={currentMedia ? currentMedia.name : (defaultLabel ?? "Default")}
              sx={{
                ".MuiListItemText-primary": {
                  whiteSpace: "initial",
                  overflow: "initial",
                },
              }}
            />
          </ListItemButton>
        </ListItem>
      </List>
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle sx={{ p: 0, pb: 2 }}>Select a wallpaper</DialogTitle>
        <DialogContent sx={{ minWidth: 400, p: 0 }}>
          {imageStore.list.length === 0 ? (
            <Typography>No images found</Typography>
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(min(15rem, 100%), 1fr))",
                gap: 1,
              }}
            >
              {imageStore.list.map((row) => (
                <ImageFile select={setFile} file={row} selected={value === row.id} key={row.id} />
              ))}
            </Box>
          )}
        </DialogContent>
        <Stack
          direction="row"
          sx={{
            justifyContent: "space-between",
            gap: 1,
            pt: 2,
          }}
        >
          <Button onClick={() => setOpen(false)} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={() => setFile({ id: null })}
            color="error"
            variant="outlined"
            startIcon={<Valkyrie icon={viTrashCan} />}
          >
            Remove
          </Button>
        </Stack>
      </Dialog>
    </>
  );
}

ImageSelect.propTypes = {
  value: PropTypes.number,
  id: PropTypes.string,
  label: PropTypes.string,
  select: PropTypes.func,
  domain: PropTypes.object,
  defaultLabel: PropTypes.string,
};

export default ImageSelect;
